import type {
  ConsultationFieldDefinition,
  ConsultationFieldOptionInput,
  ConsultationFieldType,
  ConsultationTemplate,
  ConsultationTemplateField,
} from '../types/Consultation';
import { formatDateInputValue } from '../../../shared/utils/localization';

export const CONSULTATION_AUTHORING_BUNDLE_VERSION = 1 as const;

export type PortableFieldDefinition = {
  exportKey: string;
  name: string;
  fieldType: ConsultationFieldType;
  description?: string;
  placeholder?: string;
  helpText?: string;
  validationJson?: string;
  uiSchemaJson?: string;
  schemaJson?: string;
  code?: string;
  isRequired?: boolean;
  defaultValue?: string;
  options?: ConsultationFieldOptionInput[];
  sortOrder?: number;
  isActive?: boolean;
};

export type PortableTemplateFieldAssignment = {
  fieldKey: string;
  sectionKey?: string;
  sortOrder: number;
  isRequired: boolean;
};

export type PortableTemplate = {
  exportKey: string;
  name: string;
  code?: string;
  domainProfileKey: string;
  subjectType?: string;
  description?: string;
  isActive?: boolean;
  fields: PortableTemplateFieldAssignment[];
};

export type ConsultationAuthoringBundle = {
  version: typeof CONSULTATION_AUTHORING_BUNDLE_VERSION;
  exportedAt: string;
  fieldDefinitions: PortableFieldDefinition[];
  templates: PortableTemplate[];
};

export type ConsultationAuthoringImportScope = 'fieldDefinitions' | 'templates';

export type ConsultationAuthoringImportSummary = {
  fieldDefinitions: {
    created: number;
    updated: number;
    skipped: number;
  };
  templates: {
    created: number;
    updated: number;
    skipped: number;
  };
  errors: string[];
};

const normalizeOptionalText = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const getAuthoringExportKey = (item: { code?: string | null; name: string }) => {
  const code = normalizeOptionalText(item.code);
  if (code) {
    return code.toLocaleLowerCase();
  }

  return item.name.trim().toLocaleLowerCase();
};

export const toPortableFieldDefinition = (definition: ConsultationFieldDefinition): PortableFieldDefinition => ({
  exportKey: getAuthoringExportKey(definition),
  name: definition.name,
  fieldType: definition.fieldType,
  description: normalizeOptionalText(definition.description),
  placeholder: normalizeOptionalText(definition.placeholder),
  helpText: normalizeOptionalText(definition.helpText),
  validationJson: normalizeOptionalText(definition.validationJson),
  uiSchemaJson: normalizeOptionalText(definition.uiSchemaJson),
  schemaJson: normalizeOptionalText(definition.schemaJson),
  code: normalizeOptionalText(definition.code),
  isRequired: definition.isRequired,
  defaultValue: normalizeOptionalText(definition.defaultValue),
  options: definition.options?.map((option) => ({
    id: option.id,
    label: option.label,
  })),
  sortOrder: definition.sortOrder,
  isActive: definition.isActive,
});

export const toPortableTemplate = (
  template: ConsultationTemplate,
  fieldDefinitionsById: Map<string, ConsultationFieldDefinition>,
): PortableTemplate => {
  const fields: PortableTemplateFieldAssignment[] = (template.fields ?? [])
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((assignment) => {
      const definition = fieldDefinitionsById.get(assignment.fieldDefinitionId);
      if (!definition) {
        return [];
      }

      const sectionKey = normalizeOptionalText(assignment.sectionKey);
      const portableAssignment: PortableTemplateFieldAssignment = {
        fieldKey: getAuthoringExportKey(definition),
        sortOrder: assignment.sortOrder,
        isRequired: assignment.isRequired,
        ...(sectionKey ? { sectionKey } : {}),
      };

      return [portableAssignment];
    });

  return {
    exportKey: getAuthoringExportKey(template),
    name: template.name,
    code: normalizeOptionalText(template.code),
    domainProfileKey: template.domainProfileKey,
    subjectType: normalizeOptionalText(template.subjectType),
    description: normalizeOptionalText(template.description),
    isActive: template.isActive,
    fields,
  };
};

export const buildFieldDefinitionsBundle = (
  definitions: ConsultationFieldDefinition[],
): ConsultationAuthoringBundle => ({
  version: CONSULTATION_AUTHORING_BUNDLE_VERSION,
  exportedAt: new Date().toISOString(),
  fieldDefinitions: definitions
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))
    .map(toPortableFieldDefinition),
  templates: [],
});

export const buildTemplatesBundle = (
  templates: ConsultationTemplate[],
  fieldDefinitions: ConsultationFieldDefinition[],
): ConsultationAuthoringBundle => {
  const fieldDefinitionsById = new Map(fieldDefinitions.map((item) => [item.id, item]));
  const referencedFieldKeys = new Set<string>();

  templates.forEach((template) => {
    template.fields?.forEach((assignment) => {
      const definition = fieldDefinitionsById.get(assignment.fieldDefinitionId);
      if (definition) {
        referencedFieldKeys.add(getAuthoringExportKey(definition));
      }
    });
  });

  const portableTemplates = templates
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((template) => toPortableTemplate(template, fieldDefinitionsById));

  const portableFieldDefinitions = fieldDefinitions
    .filter((definition) => referencedFieldKeys.has(getAuthoringExportKey(definition)))
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))
    .map(toPortableFieldDefinition);

  return {
    version: CONSULTATION_AUTHORING_BUNDLE_VERSION,
    exportedAt: new Date().toISOString(),
    fieldDefinitions: portableFieldDefinitions,
    templates: portableTemplates,
  };
};

const isFieldType = (value: unknown): value is ConsultationFieldType => (
  value === 'text'
  || value === 'longText'
  || value === 'number'
  || value === 'decimal'
  || value === 'boolean'
  || value === 'date'
  || value === 'time'
  || value === 'dateTime'
  || value === 'singleSelect'
  || value === 'multiSelect'
  || value === 'repeater'
  || value === 'attachments'
  || value === 'voice'
);

const parsePortableFieldDefinition = (value: unknown): PortableFieldDefinition | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const name = typeof record.name === 'string' ? record.name.trim() : '';
  const fieldType = record.fieldType;
  if (!name || !isFieldType(fieldType)) {
    return null;
  }

  const exportKey = typeof record.exportKey === 'string' && record.exportKey.trim()
    ? record.exportKey.trim().toLocaleLowerCase()
    : getAuthoringExportKey({ name, code: typeof record.code === 'string' ? record.code : undefined });

  const options = Array.isArray(record.options)
    ? record.options
      .map((option) => {
        if (!option || typeof option !== 'object') {
          return null;
        }

        const optionRecord = option as Record<string, unknown>;
        const label = typeof optionRecord.label === 'string' ? optionRecord.label.trim() : '';
        if (!label) {
          return null;
        }

        const id = typeof optionRecord.id === 'string' ? optionRecord.id.trim() : '';
        return id ? { id, label } : { label };
      })
      .filter((option): option is ConsultationFieldOptionInput => option !== null)
    : undefined;

  return {
    exportKey,
    name,
    fieldType,
    description: normalizeOptionalText(typeof record.description === 'string' ? record.description : undefined),
    placeholder: normalizeOptionalText(typeof record.placeholder === 'string' ? record.placeholder : undefined),
    helpText: normalizeOptionalText(typeof record.helpText === 'string' ? record.helpText : undefined),
    validationJson: normalizeOptionalText(typeof record.validationJson === 'string' ? record.validationJson : undefined),
    uiSchemaJson: normalizeOptionalText(typeof record.uiSchemaJson === 'string' ? record.uiSchemaJson : undefined),
    schemaJson: normalizeOptionalText(typeof record.schemaJson === 'string' ? record.schemaJson : undefined),
    code: normalizeOptionalText(typeof record.code === 'string' ? record.code : undefined),
    isRequired: typeof record.isRequired === 'boolean' ? record.isRequired : undefined,
    defaultValue: normalizeOptionalText(typeof record.defaultValue === 'string' ? record.defaultValue : undefined),
    options: options && options.length > 0 ? options : undefined,
    sortOrder: typeof record.sortOrder === 'number' ? record.sortOrder : undefined,
    isActive: typeof record.isActive === 'boolean' ? record.isActive : undefined,
  };
};

const parsePortableTemplateFieldAssignment = (value: unknown): PortableTemplateFieldAssignment | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const fieldKey = typeof record.fieldKey === 'string' ? record.fieldKey.trim().toLocaleLowerCase() : '';
  if (!fieldKey) {
    return null;
  }

  return {
    fieldKey,
    sectionKey: normalizeOptionalText(typeof record.sectionKey === 'string' ? record.sectionKey : undefined),
    sortOrder: typeof record.sortOrder === 'number' ? record.sortOrder : 0,
    isRequired: Boolean(record.isRequired),
  };
};

const parsePortableTemplate = (value: unknown): PortableTemplate | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const name = typeof record.name === 'string' ? record.name.trim() : '';
  const domainProfileKey = typeof record.domainProfileKey === 'string' ? record.domainProfileKey.trim() : '';
  if (!name || !domainProfileKey) {
    return null;
  }

  const exportKey = typeof record.exportKey === 'string' && record.exportKey.trim()
    ? record.exportKey.trim().toLocaleLowerCase()
    : getAuthoringExportKey({ name, code: typeof record.code === 'string' ? record.code : undefined });

  const fields = Array.isArray(record.fields)
    ? record.fields
      .map(parsePortableTemplateFieldAssignment)
      .filter((assignment): assignment is PortableTemplateFieldAssignment => assignment !== null)
    : [];

  return {
    exportKey,
    name,
    code: normalizeOptionalText(typeof record.code === 'string' ? record.code : undefined),
    domainProfileKey,
    subjectType: normalizeOptionalText(typeof record.subjectType === 'string' ? record.subjectType : undefined),
    description: normalizeOptionalText(typeof record.description === 'string' ? record.description : undefined),
    isActive: typeof record.isActive === 'boolean' ? record.isActive : undefined,
    fields,
  };
};

export const parseConsultationAuthoringBundle = (value: unknown): ConsultationAuthoringBundle => {
  if (!value || typeof value !== 'object') {
    throw new Error('Import file must contain a JSON object.');
  }

  const record = value as Record<string, unknown>;
  if (record.version !== CONSULTATION_AUTHORING_BUNDLE_VERSION) {
    throw new Error('Unsupported consultation authoring bundle version.');
  }

  const fieldDefinitions = Array.isArray(record.fieldDefinitions)
    ? record.fieldDefinitions
      .map(parsePortableFieldDefinition)
      .filter((definition): definition is PortableFieldDefinition => definition !== null)
    : [];

  const templates = Array.isArray(record.templates)
    ? record.templates
      .map(parsePortableTemplate)
      .filter((template): template is PortableTemplate => template !== null)
    : [];

  if (fieldDefinitions.length === 0 && templates.length === 0) {
    throw new Error('Import file does not contain field definitions or templates.');
  }

  return {
    version: CONSULTATION_AUTHORING_BUNDLE_VERSION,
    exportedAt: typeof record.exportedAt === 'string' ? record.exportedAt : new Date().toISOString(),
    fieldDefinitions,
    templates,
  };
};

export const downloadConsultationAuthoringBundle = (bundle: ConsultationAuthoringBundle, fileName: string) => {
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const buildAuthoringBundleFileName = (prefix: 'field-definitions' | 'templates') => {
  const date = formatDateInputValue(new Date());
  return `consultation-${prefix}-${date}.json`;
};

export const findTemplateFieldAssignment = (
  assignments: ConsultationTemplateField[] | undefined,
  fieldDefinitionId: string,
) => assignments?.find((assignment) => assignment.fieldDefinitionId === fieldDefinitionId);
